import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "The 'fight, flight, or freeze' response is most helpful when:",
    options: [
      "You're worried about work performance",
      "You face genuine physical danger",
      "You're thinking about past mistakes",
      "You're planning for the future"
    ],
    correctAnswer: 1,
    explanation: "The stress response is designed for immediate physical threats. It's problematic when triggered by everyday stressors that don't require this reaction."
  },
  {
    question: "Which technique directly calms the nervous system?",
    options: [
      "Analyzing your worries",
      "Deep, slow breathing",
      "Thinking about what went wrong",
      "Avoiding the stressful situation"
    ],
    correctAnswer: 1,
    explanation: "Slow, intentional breathing activates the parasympathetic nervous system, which creates the body's natural relaxation response."
  },
  {
    question: "The anxiety loop typically includes all of the following EXCEPT:",
    options: [
      "Worry thoughts",
      "Physical symptoms",
      "Avoidance of feared situations",
      "Complete relief of anxiety symptoms"
    ],
    correctAnswer: 3,
    explanation: "Avoidance actually strengthens anxiety because it prevents you from learning that the feared outcome usually doesn't happen. Relief comes from facing fears, not avoiding them."
  },
  {
    question: "To effectively break an anxiety loop, you should:",
    options: [
      "Avoid thinking about the problem",
      "Wait until you feel no anxiety to take action",
      "Gradually face the feared situation while using calming techniques",
      "Accept that anxiety will never decrease"
    ],
    correctAnswer: 2,
    explanation: "Exposure combined with coping techniques helps your nervous system learn that the situation is safe, gradually reducing anxiety."
  },
  {
    question: "A personal stress management toolkit should include:",
    options: [
      "Only one technique that works for everyone",
      "Multiple techniques for physical, mental, and social stress relief",
      "Avoidance of all stressful situations",
      "Medication as the primary solution"
    ],
    correctAnswer: 1,
    explanation: "Different situations require different approaches. A comprehensive toolkit with varied techniques gives you flexibility and better outcomes."
  }
];

const EmotioPsychoRationalModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('emotioPsychoRationalM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/emotio-psycho-rational-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                How stress and anxiety affect the body and mind
              </li>
              <li>
                Practical stress management techniques
              </li>
              <li>
                How to break the anxiety loop
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Practice your stress management techniques regularly for best results!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Emotio-Psycho-Rational Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left emotio-psycho-rational-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Emotional Intelligence and Self-Awareness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about emotional intelligence and self-awareness.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>
            
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule2QuizPage;


